import { Fragment, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGetCurrentUserQuery, useGetSlaReportQuery } from '@/services/api';
import type { SlaReportRow } from '@/types/api';


const RANGES = [
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: 0 }
] as const;

const STAGE_ORDER: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'explore', label: 'Explore' },
  { id: 'plan', label: 'Plan' },
  { id: 'code', label: 'Code' },
  { id: 'review', label: 'Review' },
  { id: 'test', label: 'Test' }
];


function fmtTat(seconds: number): string {
  if (seconds <= 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return (parts[0]![0]! + (parts[1]?.[0] ?? '')).toUpperCase();
}

const PAGE_SIZES = [10, 25, 50, 100] as const;

export function SlaReportPage(): JSX.Element {
  const userQ = useGetCurrentUserQuery();
  const [rangeKey, setRangeKey] = useState<(typeof RANGES)[number]['key']>('30');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0]);
  const [page, setPage] = useState(0);
  const toggleProject = (project: string): void => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(project)) next.add(project);
      return next;
    });
  };
  const range = RANGES.find((r) => r.key === rangeKey)!;
  const from = useMemo(() => {
    if (range.days === 0) return undefined;
    return new Date(Date.now() - range.days * 86_400_000).toISOString();
  }, [range]);

  const { data, isLoading } = useGetSlaReportQuery(from ? { from } : {});

  const rows = data?.rows ?? [];
  const summary = data?.summary;
  const grouped = useMemo(() => {
    const byProject = new Map<string, SlaReportRow[]>();
    for (const r of rows) {
      const list = byProject.get(r.project_name) ?? [];
      list.push(r);
      byProject.set(r.project_name, list);
    }
    return [...byProject.entries()];
  }, [rows]);

  const pageCount = Math.max(1, Math.ceil(grouped.length / pageSize));
  const pageStart = page * pageSize;
  const visibleGroups = grouped.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [page, pageCount]);

  if (!userQ.data) {
    return <div className="content" style={{ paddingTop: 48 }}>Loading…</div>;
  }
  if (userQ.data.role !== 'admin') {
    return (
      <>
        <h1 className="page-title">SLA &amp; TAT</h1>
        <div className="card">
          <div className="empty" style={{ padding: '80px 24px' }}>
            <p className="empty-title">Admin only</p>
            <p className="empty-sub">The SLA &amp; TAT report is restricted to administrators.</p>
          </div>
        </div>
      </>
    );
  }

  const exportCsv = (): void => {
    const header = [
      'Name',
      'Email',
      'Project',
      'Avg TAT',
      'Avg TAT (seconds)',
      'Action taken',
      'SLA breached',
      ...STAGE_ORDER.map((s) => `${s.label} breaches`)
    ];
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push(
        [
          r.approver_name,
          r.approver_email,
          r.project_name,
          r.avg_tat_seconds > 0 ? fmtTat(r.avg_tat_seconds) : '—',
          r.avg_tat_seconds,
          r.decided_count,
          r.breach_count,
          ...STAGE_ORDER.map((s) => r.by_stage?.[s.id] ?? 0)
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(',')
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sla-tat-report-${rangeKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 className="page-title">SLA &amp; TAT</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <select
            className="select sm-sel"
            aria-label="Date range"
            value={rangeKey}
            onChange={(e) => {
              setRangeKey(e.target.value as typeof rangeKey);
              setPage(0);
            }}
            style={{ height: 34 }}
          >
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
            style={{ height: 34, whiteSpace: 'nowrap' }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {summary && (
        <div
          style={{
            display: 'flex',
            gap: 12,
            margin: '18px 0',
            flexWrap: 'wrap'
          }}
        >
          <SummaryStat v={summary.project_count} k="projects" />
          <SummaryStat v={summary.approver_count} k="approvers" />
          <SummaryStat v={fmtTat(summary.avg_tat_seconds)} k="avg TAT" />
          <SummaryStat v={summary.total_breaches} k="total breaches" danger={summary.total_breaches > 0} />
        </div>
      )}

      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 12px)', overflow: 'hidden' }}>
        <table className="sla-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th style={{ minWidth: 170 }}>Assigned stages</th>
                <th style={{ width: 110, textAlign: 'right' }}>Action taken</th>
                <th style={{ width: 100, textAlign: 'right' }}>Avg. TAT</th>
                <th style={{ width: 140, textAlign: 'right', whiteSpace: 'nowrap' }}>SLA breached</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: pageSize }, (_, i) => (
                  <tr key={`sk-${i}`} aria-busy="true">
                    {Array.from({ length: 6 }, (_, j) => (
                      <td key={j}>
                        <div
                          className="animate-skel"
                          style={{
                            height: 12,
                            borderRadius: 3,
                            background: 'var(--surface-sunken)',
                            width: `${45 + ((i + j) % 4) * 12}%`
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>No approver activity in this window.</td></tr>
              ) : (
                visibleGroups.map(([project, projectRows], groupIndex) => {
                  const isCollapsed = collapsed.has(project);
                  return (
                  <Fragment key={`grp-${project}`}>
                    {groupIndex > 0 && (
                      <tr aria-hidden="true">
                        <td colSpan={6} style={{ height: 14, padding: 0, border: 'none', background: 'transparent' }} />
                      </tr>
                    )}
                    <tr>
                      <td colSpan={6} style={{ fontWeight: 600, fontSize: 12.5, background: 'var(--surface-alt)', color: 'var(--text)', padding: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                          <button
                            type="button"
                            onClick={() => toggleProject(project)}
                            aria-expanded={!isCollapsed}
                            title={isCollapsed ? 'Expand project' : 'Collapse project'}
                            data-testid="sla-project-toggle"
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, flex: 1, textAlign: 'left',
                              background: 'none', border: 'none', cursor: 'pointer', color: 'inherit',
                              font: 'inherit', padding: '8px 12px'
                            }}
                          >
                            <span
                              aria-hidden="true"
                              style={{
                                display: 'inline-block', fontSize: 10, color: 'var(--text-muted)',
                                transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                                transition: 'transform 120ms ease'
                              }}
                            >
                              ▼
                            </span>
                            <span>
                              <span style={{ color: 'var(--text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11 }}>Project&nbsp;·&nbsp;</span>{project}
                              {isCollapsed && (
                                <span style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: 11, marginLeft: 8 }}>
                                  ({projectRows.length} approver{projectRows.length === 1 ? '' : 's'} hidden)
                                </span>
                              )}
                            </span>
                          </button>
                          {(projectRows[0]?.project_breach_count ?? 0) > 0 && (
                            <span
                              title="Total SLA breaches for this project"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 10px', marginRight: 12, borderRadius: 999, background: 'rgba(229, 100, 91, 0.12)', color: 'var(--red)', fontSize: 11, fontWeight: 600, letterSpacing: 0.2, whiteSpace: 'nowrap' }}
                            >
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--red)' }} />
                              {projectRows[0]!.project_breach_count} breach{projectRows[0]!.project_breach_count === 1 ? '' : 'es'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && projectRows.map((r) => {
                      const noApprover = r.approver_user_id === '';
                      const decidedZero = noApprover || r.decided_count === 0;
                      const tatZero = noApprover || r.avg_tat_seconds === 0;
                      return (
                      <tr key={`${r.project_id}-${r.approver_user_id || 'none'}`}>
                        <td>
                          {noApprover ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 10px', borderRadius: 999, background: 'rgba(255, 166, 82, 0.16)', color: 'var(--orange)', fontSize: 11.5, fontWeight: 600, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
                              No approver assigned
                            </span>
                          ) : (
                            <div className="member-cell">
                              <span className="member-avatar alt">{initials(r.approver_name)}</span>
                              {r.approver_name}
                            </div>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{noApprover ? '—' : r.approver_email}</td>
                        <td>
                          {noApprover ? (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          ) : (
                            <StageCoverage assigned={r.assigned_stages ?? []} />
                          )}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: decidedZero ? 'var(--text-muted)' : 'var(--text)' }}>
                          {decidedZero ? '—' : r.decided_count}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: tatZero ? 'var(--text-muted)' : 'var(--text)' }}>
                          {tatZero ? '—' : fmtTat(r.avg_tat_seconds)}
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.breach_count > 0 ? 'var(--red)' : 'var(--text-muted)', fontWeight: r.breach_count > 0 ? 600 : 400 }}>
                          <BreachCount count={r.breach_count} assigned={r.assigned_stages ?? []} byStage={r.by_stage} />
                        </td>
                      </tr>
                      );
                    })}
                  </Fragment>
                  );
                })
              )}
            </tbody>
          </table>

          {!isLoading && grouped.length > 0 ? (
            <div className="pagination">
              <span className="pager-info">
                {`${pageStart + 1}–${pageStart + visibleGroups.length} of ${grouped.length} projects`}
              </span>
              <div className="pager-controls" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span className="mono-cap">Per page</span>
                <select
                  className="select sm-sel"
                  style={{ width: 80 }}
                  aria-label="Projects per page"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                >
                  {PAGE_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <button
                  className="btn ghost sm"
                  disabled={page === 0}
                  onClick={() => setPage((n) => Math.max(0, n - 1))}
                >
                  ← Prev
                </button>
                <span className="pager-info">
                  Page {page + 1} of {pageCount}
                </span>
                <button
                  className="btn ghost sm"
                  disabled={page >= pageCount - 1}
                  onClick={() => setPage((n) => Math.min(pageCount - 1, n + 1))}
                >
                  Next →
                </button>
              </div>
            </div>
          ) : null}
      </div>
    </>
  );
}

function StageCoverage({ assigned }: { assigned: string[] }): JSX.Element {
  if (assigned.length === 0) {
    return <span className="stage-none">No stage assigned</span>;
  }
  const labels = STAGE_ORDER.filter((s) => assigned.includes(s.id)).map((s) => s.label);
  if (labels.length > 3) {
    return (
      <span className="stage-count-badge" title={labels.join(', ')}>
        {labels.length}/{STAGE_ORDER.length} stages
      </span>
    );
  }
  return (
    <span className="stage-chips">
      {labels.map((l) => (
        <span key={l} className="stage-chip">{l}</span>
      ))}
    </span>
  );
}

function BreachCount({ count, assigned, byStage }: { count: number; assigned: string[]; byStage?: Record<string, number> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [flipUp, setFlipUp] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; bottom: number; right: number } | null>(null);
  if (count === 0) return <>—</>;
  const breakdown = STAGE_ORDER.filter((s) => (byStage?.[s.id] ?? 0) > 0).map((s) => ({
    label: s.label,
    count: byStage![s.id]!,
    unassigned: !assigned.includes(s.id)
  }));
  if (breakdown.length === 0) return <>{count}</>;
  const onEnter = (e: React.MouseEvent<HTMLSpanElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const estHeight = 40 + breakdown.length * 22;
    setFlipUp(window.innerHeight - rect.bottom < estHeight + 16);
    setAnchor({ top: rect.top, bottom: rect.bottom, right: window.innerWidth - rect.right });
    setOpen(true);
  };
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', cursor: 'help' }}
      onMouseEnter={onEnter}
      onMouseLeave={() => setOpen(false)}
    >
      <span style={{ borderBottom: '1px dotted var(--red)' }}>{count}</span>
      {open && anchor && createPortal(
        <span
          role="tooltip"
          style={{
            position: 'fixed',
            ...(flipUp
              ? { bottom: window.innerHeight - anchor.top + 4 }
              : { top: anchor.bottom + 4 }),
            right: anchor.right,
            zIndex: 1000,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius, 8px)',
            padding: '8px 10px',
            minWidth: 150,
            textAlign: 'left',
            fontWeight: 400,
            color: 'var(--text)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)'
          }}
        >
          {breakdown.map((b) => (
            <span key={b.label} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 12, lineHeight: 1.7 }}>
              <span style={{ color: 'var(--text-muted)' }}>
                {b.label}
                {b.unassigned ? <span style={{ fontSize: 10, marginLeft: 4 }}>(unassigned)</span> : null}
              </span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--red)', fontWeight: 600 }}>{b.count}</span>
            </span>
          ))}
        </span>,
        document.body
      )}
    </span>
  );
}

function SummaryStat({ v, k, danger = false }: { v: string | number; k: string; danger?: boolean }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '14px 20px',
        minWidth: 120,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius, 8px)'
      }}
    >
      <span style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, color: danger ? 'var(--red)' : 'var(--text)' }}>{v}</span>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</span>
    </div>
  );
}
